// Topical Coverage Engine — a Power Tool tab that compares YOUR site's page coverage against up to
// five competitors AND surfaces page ideas from Google's "People Also Ask". The first section
// prepopulates the competitor fields + location + keywords from the cover (all editable). "Run Crawl":
//   • Page Inventory — /api/page-inventory crawls each site's own URLs and categorizes them, returning
//     the same "Page Inventory — Where You're Missing vs. Competitors" table as the Competitors tab.
//   • Page Ideas (People Also Ask) — /api/paa-questions pulls the PAA questions Google shows for each
//     keyword (primary + additional), searched from the location, deduped across keywords (exact match
//     server-side, plus near-duplicate collapsing here) so each surfaced question is a distinct idea.
//   • Competitor Pages You Don't Have — the same crawl returns, per competitor, the pages on topics
//     your site doesn't cover (Claude semantic match server-side). Clicking a competitor opens a
//     popup of those pages; "Create Page Card" adds a New Page Details card (tce-{N}) below, rendered via
//     AuditSection with the Content Drawer's snapshot/restore + trash pattern. Deleting a card sets
//     its row's button back to "Create Page Card".

import { useState, useEffect, useCallback, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection, removeCreateDraft } from "./AuditSection";
import { confirmDeletePopover } from "../lib/confirmPopover";
import { loadAnthropicKey, loadDataForSeoCredentials } from "./SettingsModal";
import { CityPicker, compactLoc } from "./CityPicker";
import { scheduleFlush } from "../lib/siteData";

interface Props {
  siteUrl?: string;
  // Passed straight through to the AuditSection that renders the Create Page cards.
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
  getClient?: () => import("../lib/wpClient").WPClient | null;
}

// Per-site working data (all members of AUDIT_KEYS so they swap with the active site).
const COMP_KEY = "lsg_tce_competitors";   // the 5 competitor-domain inputs (JSON string[])
const RESULT_KEY = "lsg_tce_result";      // the last Page Inventory table HTML
const LOC_KEY = "lsg_tce_location";       // location searched from
const PRIMARY_KEY = "lsg_tce_primary_kw"; // primary keyword
const EXTRA_KEY = "lsg_tce_extra_kws";    // additional target keywords (comma-separated)
const PAA_KEY = "lsg_tce_paa";            // last PAA questions (JSON PaaQuestion[])
const AC_KEY = "lsg_tce_ac";              // last Autocomplete/Related queries (JSON AcQuery[])
const GAPS_KEY = "lsg_tce_gaps";          // competitor pages on topics you don't cover (JSON CompetitorGaps[])
const TITLES_KEY = "lsg_tce_titles";      // competitor page url → <title> cache (JSON)
const CARDS_KEY = "lsg_tce_cards";        // Create Page card HTML
const SNAP_KEY = "lsg_tce_snapshot";      // live card edits, snapshotted across tab switches
const SEQ_KEY = "lsg_tce_seq";            // monotonic card counter — a new card never reuses a deleted id
const CREATED_KEY = "lsg_tce_created";    // competitor page url → its card's fixId (JSON)
const FIELD_IDS = ["title", "mt", "sl", "par", "md", "pt", "pd", "aft", "kw", "wc", "type", "ci", "summaryheading"] as const;

const MAX_EXTRA_KEYWORDS = 5;
const COUNTRY = "United States"; // the city library is filtered by country; this app is US-focused

interface PaaQuestion { question: string; sourceKeyword: string }
interface AcQuery { query: string; sourceKeyword: string; source: "autocomplete" | "related" }
interface LocPick { name: string; code: number } // DataForSEO location (name is the location_name)
interface GapPage { url: string; category: string }
interface CompetitorGaps { domain: string; pages: GapPage[] }

const readJson = <T,>(key: string, fallback: T): T => { try { return (JSON.parse(localStorage.getItem(key) ?? "null") as T) ?? fallback; } catch { return fallback; } };

// "/services/teen-drug-rehab/" → "Teen Drug Rehab" — shown until the real <title> loads.
function slugTitle(url: string): string {
  let path = url;
  try { path = new URL(url).pathname; } catch { /* keep */ }
  const last = path.split("/").filter(Boolean).pop() ?? "";
  return decodeURIComponent(last).replace(/\.[a-z]{2,4}$/i, "").replace(/[-_]+/g, " ").trim()
    .replace(/(^|\s)([a-z])/g, (_m, sp: string, c: string) => sp + c.toUpperCase()) || url;
}

// Reduce any URL/paste to a bare registrable host ("https://www.x.com/path" → "x.com").
const cleanDomain = (d: string): string =>
  (d || "").trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "").toLowerCase();

// YOUR site — the authoritative crawl domain, else derived from the cover Website URL.
function getTargetDomain(): string {
  const stored = (localStorage.getItem("seo_target_domain") ?? "").trim();
  if (stored && stored.includes(".")) return cleanDomain(stored);
  try {
    const raw = (localStorage.getItem("cover_website_url") ?? "").trim();
    if (raw) return cleanDomain(new URL(/^https?:/i.test(raw) ? raw : `https://${raw}`).hostname);
  } catch { /* ignore */ }
  return "";
}

// Up to 5 competitor domains pulled from the cover, in priority order:
//   1) the retrieved competitors (seo_competitor_domains — the authoritative list Get Competitors writes)
//   2) the cover's Custom Competitors inputs (cover_comp_1..5)
//   3) the competitor sites in seo_full_metrics (sites[1..])
function coverCompetitors(): string[] {
  const out: string[] = [];
  const push = (arr: (string | undefined)[]) => {
    for (const d of arr) { const c = cleanDomain(d ?? ""); if (c && c.includes(".") && !out.includes(c)) out.push(c); }
  };
  try { push(JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]")); } catch { /* ignore */ }
  if (out.length < 5) { const cov: string[] = []; for (let i = 1; i <= 5; i++) cov.push(localStorage.getItem(`cover_comp_${i}`) ?? ""); push(cov); }
  if (out.length < 5) {
    try {
      const m = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { sites?: Array<{ domain?: string }> } | null;
      if (m?.sites) push(m.sites.slice(1).map(s => s.domain));
    } catch { /* ignore */ }
  }
  return out.slice(0, 5);
}

// Always render exactly five competitor fields.
const toFive = (arr: string[]): string[] => { const a = arr.slice(0, 5); while (a.length < 5) a.push(""); return a; };

// The cover's additional keywords (fields 2–5), for prefilling the comma-separated input.
function coverExtraKeywords(): string {
  const out: string[] = [];
  for (let i = 2; i <= 5; i++) { const v = (localStorage.getItem(`cover_kw_${i}`) ?? "").trim(); if (v) out.push(v); }
  return out.join(", ");
}

// Collapse near-duplicate questions on top of the server's exact-match dedup. Two questions count as
// the same idea when their significant-word sets overlap heavily (Jaccard ≥ 0.8) — e.g. "How much
// does X cost?" vs "How much does X cost near me?". The first occurrence (higher keyword priority) wins.
const STOPWORDS = new Set([
  "the", "a", "an", "is", "are", "do", "does", "did", "to", "of", "for", "in", "on", "and", "or", "what",
  "how", "why", "when", "where", "which", "who", "can", "i", "you", "my", "your", "with", "at", "be", "it",
  "that", "this", "should", "will", "there", "near", "me", "get", "have", "has", "if", "about",
]);
function sigTokens(q: string): Set<string> {
  return new Set(q.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter(w => w && !STOPWORDS.has(w)));
}
function jaccard(a: Set<string>, b: Set<string>): number {
  if (!a.size && !b.size) return 1;
  let inter = 0; for (const x of a) if (b.has(x)) inter++;
  return inter / new Set([...a, ...b]).size;
}
function dedupeSimilar(questions: PaaQuestion[]): PaaQuestion[] {
  const kept: PaaQuestion[] = [];
  const keptTokens: Set<string>[] = [];
  for (const q of questions) {
    const t = sigTokens(q.question);
    if (keptTokens.some(k => jaccard(t, k) >= 0.8)) continue;
    kept.push(q); keptTokens.push(t);
  }
  return kept;
}

export function TopicalCoverageEngine(props: Props) {
  const { siteUrl } = props;
  const [target, setTarget] = useState("");
  const [comps, setComps] = useState<string[]>(["", "", "", "", ""]);
  const [prefilled, setPrefilled] = useState(false); // competitor fields came from the cover
  const [locPick, setLocPick] = useState<LocPick | null>(null);
  const [primaryKw, setPrimaryKw] = useState("");
  const [extraKws, setExtraKws] = useState("");
  const [running, setRunning] = useState(false);
  const [resultHtml, setResultHtml] = useState("");
  const [paa, setPaa] = useState<PaaQuestion[]>([]);
  const [ac, setAc] = useState<AcQuery[]>([]);
  const [status, setStatus] = useState<{ tone: "ok" | "err" | "info"; text: string } | null>(null);
  // Competitor Pages You Don't Have — gaps from the crawl, the popup, and the Create Page cards.
  const [gaps, setGaps] = useState<CompetitorGaps[]>([]);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [cardsHtml, setCardsHtml] = useState("");
  const [created, setCreated] = useState<Record<string, string>>({}); // page url → fixId
  const [openComp, setOpenComp] = useState<string | null>(null);      // domain whose popup is open
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);      // page url being built

  // Load everything on mount and whenever the active site changes. For each field, a saved tab value
  // wins; otherwise it prefills from the cover (location, primary keyword, additional keywords,
  // competitors). The app swaps the per-site localStorage bucket and remounts per site; the siteUrl
  // dep is a safety net so the fields re-seed even if the component is reused.
  useEffect(() => {
    setTarget(getTargetDomain());

    let savedComps: string[] = [];
    try { savedComps = JSON.parse(localStorage.getItem(COMP_KEY) ?? "[]"); } catch { savedComps = []; }
    const hasSavedComps = Array.isArray(savedComps) && savedComps.some(s => (s ?? "").trim());
    setComps(toFive(hasSavedComps ? savedComps : coverCompetitors()));
    setPrefilled(!hasSavedComps);

    // Location is a strict DataForSEO city pick ({name, code}); only a saved JSON object restores it
    // (a plain string from the cover can't resolve to a location code, so the user picks from the list).
    let savedLoc: LocPick | null = null;
    try { const p = JSON.parse(localStorage.getItem(LOC_KEY) ?? "null"); if (p && typeof p.name === "string" && typeof p.code === "number") savedLoc = p; } catch { savedLoc = null; }
    setLocPick(savedLoc);
    const savedPrimary = localStorage.getItem(PRIMARY_KEY);
    setPrimaryKw(savedPrimary != null ? savedPrimary : (localStorage.getItem("cover_kw_1") ?? "").trim());
    const savedExtra = localStorage.getItem(EXTRA_KEY);
    setExtraKws(savedExtra != null ? savedExtra : coverExtraKeywords());

    setResultHtml(localStorage.getItem(RESULT_KEY) ?? "");
    try { setPaa(JSON.parse(localStorage.getItem(PAA_KEY) ?? "[]")); } catch { setPaa([]); }
    try { setAc(JSON.parse(localStorage.getItem(AC_KEY) ?? "[]")); } catch { setAc([]); }
    setGaps(readJson<CompetitorGaps[]>(GAPS_KEY, []));
    setTitles(readJson<Record<string, string>>(TITLES_KEY, {}));
    setCardsHtml(localStorage.getItem(CARDS_KEY) ?? "");
    setCreated(readJson<Record<string, string>>(CREATED_KEY, {}));
    setOpenComp(null);
    setStatus(null);
  }, [siteUrl]);

  // Write to the per-site localStorage key AND flush the blob server-side — these keys are AUDIT_KEYS,
  // and on boot loadActiveAudit() re-applies the server blob over localStorage (dropping any key not in
  // it). Without the flush, a page refresh would wipe the crawled Page Inventory + PAA results.
  const persist = (key: string, v: string) => { try { localStorage.setItem(key, v); } catch { /* ignore */ } scheduleFlush(); };

  const setComp = (i: number, v: string) => {
    setPrefilled(false);
    setComps(prev => { const next = [...prev]; next[i] = v; persist(COMP_KEY, JSON.stringify(next)); return next; });
  };

  const filledComps = comps.map(cleanDomain).filter(Boolean);
  // Primary first, then the comma-separated additional keywords (capped), deduped case-insensitively.
  const keywordList = (() => {
    const raw = [primaryKw, ...extraKws.split(",").slice(0, MAX_EXTRA_KEYWORDS)].map(k => k.trim()).filter(Boolean);
    const seen = new Set<string>(); const out: string[] = [];
    for (const k of raw) { const lk = k.toLowerCase(); if (!seen.has(lk)) { seen.add(lk); out.push(k); } }
    return out;
  })();
  const extraCount = extraKws.split(",").map(k => k.trim()).filter(Boolean).length;

  const run = async () => {
    const t = getTargetDomain();
    setTarget(t);
    const uniqueComps = [...new Set(filledComps.filter(d => d !== t))];
    // All inputs are required: your site, at least one competitor, a primary keyword, and a location.
    if (!t) { setStatus({ tone: "err", text: "Set your Website URL on the cover first — it's the site we compare against." }); return; }
    if (!uniqueComps.length) { setStatus({ tone: "err", text: "Add at least one competitor domain." }); return; }
    if (!primaryKw.trim()) { setStatus({ tone: "err", text: "Enter a primary keyword." }); return; }
    if (!locPick) { setStatus({ tone: "err", text: "Select a location from the list — People Also Ask and Autocomplete search from it." }); return; }
    const { login, password } = loadDataForSeoCredentials();
    if (!login || !password) { setStatus({ tone: "err", text: "People Also Ask and Autocomplete need your DataForSEO credentials — add them in Settings." }); return; }

    const wantInventory = true;
    const wantPaa = true;
    setRunning(true);
    setStatus({ tone: "info", text: "Running — crawling competitor pages and pulling People Also Ask + autocomplete queries…" });

    const tasks: Promise<string>[] = [];

    if (wantInventory) {
      tasks.push((async () => {
        try {
          const res = await fetch("/api/page-inventory", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ domains: [t, ...uniqueComps], anthropicKey: loadAnthropicKey(), businessName: localStorage.getItem("cover_business_name") ?? "" }),
          });
          const data = await res.json() as { ok: boolean; html?: string; gaps?: CompetitorGaps[]; error?: string };
          if (!data.ok || !data.html) throw new Error(data.error || "No page inventory returned.");
          setResultHtml(data.html); persist(RESULT_KEY, data.html);
          const g = data.gaps ?? [];
          setGaps(g); persist(GAPS_KEY, JSON.stringify(g));
          return "";
        } catch (e) { return `Page Inventory: ${e instanceof Error ? e.message : "failed"}`; }
      })());
    }

    if (wantPaa) {
      tasks.push((async () => {
        try {
          const res = await fetch("/api/paa-questions", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ keywords: keywordList, location: locPick?.name ?? "", dataForSeoLogin: login, dataForSeoPassword: password, maxQuestions: 80 }),
          });
          const data = await res.json() as { ok: boolean; questions?: PaaQuestion[]; error?: string };
          if (!data.ok) throw new Error(data.error || "No questions returned.");
          const unique = dedupeSimilar(data.questions ?? []);
          setPaa(unique); persist(PAA_KEY, JSON.stringify(unique));
          return "";
        } catch (e) { return `Page Ideas: ${e instanceof Error ? e.message : "failed"}`; }
      })());

      tasks.push((async () => {
        try {
          const res = await fetch("/api/autocomplete-queries", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ keywords: keywordList, location: locPick?.name ?? "", dataForSeoLogin: login, dataForSeoPassword: password, maxQueries: 120 }),
          });
          const data = await res.json() as { ok: boolean; queries?: AcQuery[]; error?: string };
          if (!data.ok) throw new Error(data.error || "No queries returned.");
          const list = data.queries ?? [];
          setAc(list); persist(AC_KEY, JSON.stringify(list));
          return "";
        } catch (e) { return `Autocomplete: ${e instanceof Error ? e.message : "failed"}`; }
      })());
    }

    const errors = (await Promise.all(tasks)).filter(Boolean);
    setRunning(false);
    setStatus(errors.length
      ? { tone: "err", text: errors.join("  ·  ") }
      : { tone: "ok", text: "Done." });
  };

  // ── Create Page cards (mirrors the Content Drawer's snapshot/restore + trash pattern) ──
  // The cards' fields + generated content live in plain DOM (AuditSection's innerHTML), which is
  // destroyed on tab switch — snapshot it to localStorage on unmount and restore after re-render.
  const cardsRef = useRef<HTMLDivElement>(null);

  const snapshotCards = useCallback(() => {
    const root = cardsRef.current;
    if (!root) return;
    const snap: Record<string, Record<string, unknown>> = {};
    root.querySelectorAll<HTMLElement>('[id^="cft-content-"]').forEach(editor => {
      const fixId = editor.id.replace(/^cft-content-/, "");
      const fields: Record<string, string> = {};
      for (const f of FIELD_IDS) {
        const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`);
        if (el) fields[f] = el.value;
      }
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) fields.summarize = summCb.checked ? "1" : "";
      const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`);
      const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
      const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
      snap[fixId] = {
        fields,
        editor: editor.innerHTML,
        faq: editor.dataset.faqAnswer ?? "",
        open: row ? row.style.display !== "none" : false,       // the create FORM was expanded
        contentShown: wrap ? wrap.style.display !== "none" : false,
        publish: submit ? submit.style.display !== "none" : false,
      };
    });
    try { localStorage.setItem(SNAP_KEY, JSON.stringify(snap)); } catch { /* ignore */ }
  }, []);

  const restoreCards = useCallback(() => {
    const root = cardsRef.current;
    if (!root) return;
    const snap = readJson<Record<string, { fields?: Record<string, string>; editor?: string; faq?: string; open?: boolean; contentShown?: boolean; publish?: boolean }>>(SNAP_KEY, {});
    for (const [fixId, s] of Object.entries(snap)) {
      for (const f of FIELD_IDS) {
        const v = s.fields?.[f];
        if (v == null) continue;
        const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`);
        if (el) el.value = v;
      }
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) {
        summCb.checked = s.fields?.summarize === "1";
        const summWrap = root.querySelector<HTMLElement>(`#cft-summaryheading-wrap-${fixId}`);
        if (summWrap) summWrap.style.display = summCb.checked ? "block" : "none";
      }
      const editor = root.querySelector<HTMLElement>(`#cft-content-${fixId}`);
      if (editor && s.editor) {
        editor.innerHTML = s.editor;
        if (s.faq) editor.dataset.faqAnswer = s.faq;
        // Don't auto-regenerate over restored content when the card is reopened.
        const expand = root.querySelector<HTMLElement>(`.create-expand-btn[data-target="cfr-${fixId}"]`);
        if (expand) expand.dataset.autogenDone = "1";
        const gen = root.querySelector<HTMLElement>(`.generate-content-btn[data-fix-id="${fixId}"]`);
        if (gen) gen.textContent = "Regenerate Content";
      }
      if (s.contentShown) {
        const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
        if (wrap) wrap.style.display = "";
      }
      if (s.open) {
        const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`);
        if (row) row.style.display = "";
      }
      if (s.publish) {
        const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
        if (submit) submit.style.display = "";
      }
    }
  }, []);

  // Trash a card: snapshot the OTHER cards first so their content survives the re-render, drop the
  // card from the snapshot + markup, and release its competitor page so the popup row reverts to
  // "Create Page Card".
  const removeCard = useCallback((fixId: string) => {
    snapshotCards();
    const snap = readJson<Record<string, unknown>>(SNAP_KEY, {});
    delete snap[fixId];
    persist(SNAP_KEY, JSON.stringify(snap));
    removeCreateDraft(fixId); // deleting the card clears its saved draft (published cards keep theirs)
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      tmp.querySelector(`#hdr-${fixId}`)?.remove();
      tmp.querySelector(`#cfr-${fixId}`)?.remove();
      const next = tmp.innerHTML.trim();
      persist(CARDS_KEY, next);
      return next;
    });
    setCreated(prev => {
      const next = Object.fromEntries(Object.entries(prev).filter(([, id]) => id !== fixId));
      persist(CREATED_KEY, JSON.stringify(next));
      return next;
    });
  }, [snapshotCards]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => snapshotCards(), [snapshotCards]);
  useEffect(() => { if (cardsHtml) restoreCards(); }, [cardsHtml, restoreCards]);

  // Wire the per-card trash buttons + header click-to-expand (same as the Content Drawer).
  useEffect(() => {
    const root = cardsRef.current;
    if (!root) return;
    // No "✓ Complete" checkbox on these cards — strip every .ac-chk label and its separator.
    root.querySelectorAll<HTMLElement>(".um-new .ac label.ac-chk").forEach(lbl => {
      const prev = lbl.previousElementSibling;
      if (prev?.classList.contains("ac-sep")) prev.remove();
      else if (lbl.nextElementSibling?.classList.contains("ac-sep")) lbl.nextElementSibling.remove();
      lbl.remove();
    });
    const cleanups: Array<() => void> = [];
    root.querySelectorAll<HTMLElement>(".cd-trash-btn").forEach(btn => {
      const fixId = btn.getAttribute("data-fix");
      if (!fixId) return;
      const h = (e: Event) => { e.preventDefault(); confirmDeletePopover(btn, () => removeCard(fixId)); };
      btn.addEventListener("click", h);
      cleanups.push(() => btn.removeEventListener("click", h));
    });
    root.querySelectorAll<HTMLElement>(".um.um-new .um-hd").forEach(hd => {
      const onClick = (e: Event) => {
        if ((e.target as HTMLElement).closest(".ac, .cd-trash-btn, button, input, label, a")) return;
        hd.querySelector<HTMLButtonElement>(".create-expand-btn")?.click();
      };
      hd.addEventListener("click", onClick);
      cleanups.push(() => hd.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach(fn => fn());
  }, [cardsHtml, removeCard]);

  // A page counts as Created only while its card is still in the markup — so a deleted card (by
  // any path) always reverts the row to "Create Page Card".
  const cardFor = (url: string): string | null => {
    const id = created[url];
    return id && cardsHtml.includes(`id="hdr-${id}"`) ? id : null;
  };

  const createPage = async (page: GapPage) => {
    if (creating || cardFor(page.url)) return;
    setCreating(page.url);
    try {
      // Next unique tce-{n}: past every id in the markup AND the persisted counter.
      let max = -1;
      for (const m of cardsHtml.matchAll(/id="hdr-tce-(\d+)"/g)) max = Math.max(max, parseInt(m[1], 10));
      const storedSeq = parseInt(localStorage.getItem(SEQ_KEY) ?? "0", 10) || 0;
      const index = Math.max(max + 1, storedSeq);
      const fixId = `tce-${index}`;
      // Never prefill the fresh card from a stale snapshot of a deleted card with the same id.
      const snap = readJson<Record<string, unknown>>(SNAP_KEY, {});
      if (fixId in snap) { delete snap[fixId]; persist(SNAP_KEY, JSON.stringify(snap)); }
      const res = await fetch("/api/build-gap-cards", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ title: titles[page.url] || slugTitle(page.url), competitorUrl: page.url, pageType: page.category === "blog" ? "article" : "service" }],
          startIndex: index,
          // Used to write original, optimized title/meta/keyword/slug for OUR page (not the competitor's).
          anthropicKey: loadAnthropicKey(),
          businessName: localStorage.getItem("cover_business_name") ?? "",
          domain: target,
          location: (localStorage.getItem("cover_location") ?? "").trim() || (locPick?.name ?? ""),
          brand: props.siteBrand ?? "",
        }),
      });
      const data = await res.json() as { ok: boolean; html?: string; error?: string; optimized?: number };
      if (!data.ok || !data.html) throw new Error(data.error || "Couldn't build the card.");
      if (!data.optimized) setStatus({ tone: "info", text: "Card created with the competitor's page title as a starting point — add an Anthropic API key in Settings to get original, SEO-optimized title, meta, keyword and slug." });
      persist(SEQ_KEY, String(index + 1));
      snapshotCards(); // keep in-progress edits on the existing cards through the re-render
      const nextHtml = (cardsHtml ? cardsHtml + "\n" : "") + data.html;
      setCardsHtml(nextHtml); persist(CARDS_KEY, nextHtml);
      const nextCreated = { ...created, [page.url]: fixId };
      setCreated(nextCreated); persist(CREATED_KEY, JSON.stringify(nextCreated));
    } catch (e) {
      setStatus({ tone: "err", text: `Create Page Card: ${e instanceof Error ? e.message : "failed"}` });
    } finally {
      setCreating(null);
    }
  };

  // Open a competitor's popup and fill in real page titles (slug-derived until they arrive).
  const openCompetitor = async (domain: string) => {
    setOpenComp(domain);
    const pages = gaps.find(g => g.domain === domain)?.pages ?? [];
    const missing = pages.map(p => p.url).filter(u => !(u in titles)).slice(0, 150);
    if (!missing.length) return;
    setTitlesLoading(true);
    try {
      const res = await fetch("/api/page-titles", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ urls: missing }) });
      const data = await res.json() as { ok: boolean; titles?: Record<string, string> };
      if (data.ok && data.titles) {
        // Cache misses as "" so a title-less page isn't re-fetched on every open.
        const fetched = Object.fromEntries(missing.map(u => [u, data.titles![u] ?? ""]));
        setTitles(prev => { const next = { ...prev, ...fetched }; persist(TITLES_KEY, JSON.stringify(next)); return next; });
      }
    } catch { /* keep slug titles */ } finally {
      setTitlesLoading(false);
    }
  };

  useEffect(() => {
    if (!openComp) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenComp(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openComp]);

  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const inkMuted = isDark ? "#93a4c0" : "#64748b";
  const field: CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6,
    border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", fontSize: 12.5,
    outline: "none", fontFamily: "'Inter','Mulish',sans-serif",
  };
  const fieldLabel: CSSProperties = { fontSize: 11, fontWeight: 700, color: inkMuted, minWidth: 100, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".6px" };
  const statusColor = status?.tone === "err" ? (isDark ? "#f87171" : "#dc2626") : status?.tone === "ok" ? "#16a34a" : inkMuted;
  // Required to run: your site, ≥1 competitor, a primary keyword, and a selected location.
  const canRun = !!target && filledComps.length > 0 && !!primaryKw.trim() && !!locPick;
  const reqStar = <span style={{ color: isDark ? "#f87171" : "#dc2626", fontWeight: 700 }}> *</span>;

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div className="sh"><h2>Topical Coverage Engine</h2><p>See where your page coverage trails competitors, and find page ideas from People Also Ask</p></div>

      {/* Inputs — prepopulated from the cover, editable. */}
      <div className="cd-section">
        <div className="cd-section-hd">Competitors &amp; Keywords</div>
        <p className="cd-help" style={{ marginBottom: 14 }}>
          {prefilled && filledComps.length > 0
            ? <>Prepopulated from your cover. Edit any field, then Run Crawl — Page Inventory compares your pages to the competitors, and Page Ideas + Autocomplete pull People Also Ask and autocomplete queries for your keywords searched from the location.</>
            : <>Enter your competitors, a location, and keywords, then Run Crawl.</>}
          {" "}<strong style={{ color: ink }}>Required:</strong> your site, at least one competitor, a primary keyword, and a location (marked <span style={{ color: isDark ? "#f87171" : "#dc2626", fontWeight: 700 }}>*</span>).
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ ...fieldLabel, color: "#16a34a" }}>You</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: ink, fontFamily: "'Inter','Mulish',sans-serif" }}>
            {target || <em style={{ color: inkMuted, fontWeight: 400 }}>Set your Website URL on the cover</em>}
          </span>
        </div>

        <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
          {comps.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={fieldLabel}>Comp {i + 1}{i === 0 ? reqStar : null}</span>
              <input value={c} onChange={e => setComp(i, e.target.value)} placeholder={i === 0 ? "competitor.com (required)" : "competitor.com"} spellCheck={false} style={field} />
            </div>
          ))}
        </div>

        <div style={{ height: 1, background: isDark ? "rgba(255,255,255,.1)" : "#e2e8f0", margin: "18px 0", maxWidth: 560 }} />

        {/* Location + keywords drive the People Also Ask page-idea search. */}
        <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={fieldLabel}>Location{reqStar}</span>
            <div style={{ flex: 1 }}>
              <CityPicker
                country={COUNTRY}
                value={locPick}
                onChange={v => { setLocPick(v); persist(LOC_KEY, v ? JSON.stringify(v) : ""); }}
                isDark={isDark}
                inpStyle={field}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={fieldLabel}>Primary KW{reqStar}</span>
            <input value={primaryKw} onChange={e => { setPrimaryKw(e.target.value); persist(PRIMARY_KEY, e.target.value); }} placeholder="your main target keyword (required)" spellCheck={false} style={field} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={fieldLabel}>Additional KWs</span>
            {/* Tooltip lives on the wrapper (pseudo-elements don't render on <input>); .tip-show forces
                it visible — it pops up automatically once the 5-keyword limit is reached. */}
            <div
              style={{ flex: 1 }}
              className={extraCount >= MAX_EXTRA_KEYWORDS ? "tip-show" : undefined}
              data-tip={extraCount >= MAX_EXTRA_KEYWORDS ? `Keyword limit reached — ${MAX_EXTRA_KEYWORDS} additional keywords maximum. Any beyond ${MAX_EXTRA_KEYWORDS} are ignored.` : undefined}
            >
              <input
                value={extraKws}
                onChange={e => { setExtraKws(e.target.value); persist(EXTRA_KEY, e.target.value); }}
                placeholder="additional keywords, comma-separated (up to 5)"
                spellCheck={false}
                style={field}
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
          <button
            onClick={() => void run()}
            disabled={running || !canRun}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", border: "none", borderRadius: 0,
              fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: running || !canRun ? "default" : "pointer",
              background: running || !canRun ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)",
              whiteSpace: "nowrap",
            }}
          >
            {running && <span className="lsg-spinner" style={{ borderColor: "rgba(255,255,255,.45)", borderTopColor: "#fff" }} />}
            {running ? "Crawling…" : "Run Crawl"}
          </button>
          {status && <span style={{ fontSize: 12.5, fontWeight: 600, color: statusColor, fontFamily: "'Inter','Mulish',sans-serif" }}>{status.text}</span>}
        </div>
      </div>

      {/* Page Inventory result — the same table shown on the Competitors tab. */}
      {resultHtml && (
        <div className="cd-section" style={{ marginTop: 18 }}>
          <div dangerouslySetInnerHTML={{ __html: resultHtml }} />
        </div>
      )}

      {/* Competitor Pages You Don't Have — one tile per competitor; click to see its pages. */}
      {gaps.length > 0 && (
        <div className="cd-section" style={{ marginTop: 18 }}>
          <div className="cd-section-hd">Competitor Pages You Don&rsquo;t Have</div>
          <p className="cd-help" style={{ marginBottom: 14 }}>
            Pages each competitor has on topics your site doesn&rsquo;t cover yet (pages similar in topic to yours are left out). Click a competitor to see its pages, then Create Page Card to add a page card below.
          </p>
          <div className="tce-comp-row">
            {gaps.map(g => (
              <button key={g.domain} type="button" className="tce-comp-tile" onClick={() => void openCompetitor(g.domain)} disabled={g.pages.length === 0}>
                <span className="tce-comp-domain">{g.domain}</span>
                <span className="tce-comp-count">{g.pages.length} page{g.pages.length === 1 ? "" : "s"}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Create Page cards — rendered through AuditSection so they get the full create-form wiring
          (Generate Optimized Content → editor → Publish to WordPress). */}
      {cardsHtml && (
        <div className="cd-cards" ref={cardsRef} style={{ marginTop: 18 }}>
          <AuditSection
            id="topicalcoverage"
            html={cardsHtml}
            state={props.state}
            siteBrand={props.siteBrand}
            auditDate={props.auditDate}
            onApprove={props.onApprove}
            onIgnore={props.onIgnore}
            onDone={props.onDone}
            onReset={props.onReset}
            hidden={props.hidden}
            onToggleHide={props.onToggleHide}
            connected={props.connected}
            fetchPageTags={props.fetchPageTags}
            onOpenSchemaSetup={props.onOpenSchemaSetup}
            getClient={props.getClient}
          />
        </div>
      )}

      {/* Competitor pages popup. */}
      {openComp && createPortal(
        <div className="tce-modal-overlay" onClick={() => setOpenComp(null)}>
          <div className="tce-modal" role="dialog" aria-modal="true" aria-label={`${openComp} pages`} onClick={e => e.stopPropagation()}>
            <div className="tce-modal-hd">
              <div>
                <div className="tce-modal-title">{openComp}</div>
                <div className="tce-modal-sub">
                  {(gaps.find(g => g.domain === openComp)?.pages.length ?? 0)} pages on topics your site doesn&rsquo;t cover{titlesLoading ? " · loading page titles…" : ""}
                </div>
              </div>
              <button type="button" className="tce-modal-close" onClick={() => setOpenComp(null)} aria-label="Close">✕</button>
            </div>
            <div className="tce-modal-body">
              <table className="tce-pages">
                <thead><tr><th>Page Title</th><th>Page URL</th><th>Create Content Card</th></tr></thead>
                <tbody>
                  {(gaps.find(g => g.domain === openComp)?.pages ?? []).map(p => {
                    const done = !!cardFor(p.url);
                    return (
                      <tr key={p.url}>
                        <td className="tce-pg-title">{titles[p.url] || slugTitle(p.url)}</td>
                        <td className="tce-pg-url"><a href={p.url} target="_blank" rel="noopener noreferrer">{p.url.replace(/^https?:\/\/(www\.)?/, "")}</a></td>
                        <td className="tce-pg-act">
                          <button type="button" className={`tce-create-btn${done ? " is-created" : ""}`} disabled={done || creating === p.url} onClick={() => void createPage(p)}>
                            {done ? "✓ Created" : creating === p.url ? "Creating…" : "Create Page Card"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Page Ideas — People Also Ask questions (deduped across keywords). */}
      {(paa.length > 0 || (running && keywordList.length > 0)) && (
        <div className="cd-section" style={{ marginTop: 18 }}>
          <div className="cd-section-hd">Page Ideas — People Also Ask</div>
          <p className="cd-help" style={{ marginBottom: 14 }}>
            Real &ldquo;People Also Ask&rdquo; questions Google shows for your keywords{locPick ? <> searched from <strong>{compactLoc(locPick.name)}</strong></> : null} — each a page a searcher is actively looking for. Near-duplicate questions are collapsed, so every row is a distinct idea.
          </p>
          {paa.length === 0 && running ? (
            <p className="cd-help">Searching…</p>
          ) : paa.length === 0 ? (
            <p className="cd-help">No People Also Ask questions were returned for these keywords.</p>
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: inkMuted, marginBottom: 10 }}>{paa.length} unique question{paa.length === 1 ? "" : "s"}</div>
              <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: 8 }}>
                {paa.map((q, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "9px 12px", borderRadius: 8, background: isDark ? "rgba(255,255,255,.03)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,.08)" : "#eef2f6"}` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: inkMuted, minWidth: 22, textAlign: "right", fontFamily: "'Roboto Mono', monospace" }}>{i + 1}</span>
                    <span style={{ flex: 1, fontSize: 13, color: ink, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif" }}>{q.question}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: inkMuted, whiteSpace: "nowrap", background: isDark ? "rgba(255,255,255,.06)" : "#eef2f6", borderRadius: 999, padding: "2px 9px" }}>{q.sourceKeyword}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}

      {/* Autocomplete — Google Autocomplete suggestions + the SERP's Related searches. */}
      {(ac.length > 0 || (running && keywordList.length > 0)) && (
        <div className="cd-section" style={{ marginTop: 18 }}>
          <div className="cd-section-hd">Autocomplete &amp; Related Searches</div>
          <p className="cd-help" style={{ marginBottom: 14 }}>
            Real queries from Google&rsquo;s autocomplete dropdown and the SERP&rsquo;s &ldquo;Related searches&rdquo; for your keywords{locPick ? <> searched from <strong>{compactLoc(locPick.name)}</strong></> : null} — the exact phrasing people type, useful for titles, headings, and new page targets. Deduped across keywords.
          </p>
          {ac.length === 0 && running ? (
            <p className="cd-help">Searching…</p>
          ) : ac.length === 0 ? (
            <p className="cd-help">No autocomplete or related-search queries were returned for these keywords.</p>
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: inkMuted, marginBottom: 10 }}>{ac.length} unique quer{ac.length === 1 ? "y" : "ies"}</div>
              <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: 8 }}>
                {ac.map((q, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "9px 12px", borderRadius: 8, background: isDark ? "rgba(255,255,255,.03)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,.08)" : "#eef2f6"}` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: inkMuted, minWidth: 22, textAlign: "right", fontFamily: "'Roboto Mono', monospace" }}>{i + 1}</span>
                    <span style={{ flex: 1, fontSize: 13, color: ink, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif" }}>{q.query}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px", color: q.source === "autocomplete" ? (isDark ? "#93c5fd" : "#2563eb") : (isDark ? "#c4b5fd" : "#7c3aed"), whiteSpace: "nowrap", background: isDark ? "rgba(255,255,255,.06)" : "#eef2f6", borderRadius: 999, padding: "2px 9px" }}>{q.source === "autocomplete" ? "Autocomplete" : "Related"}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: inkMuted, whiteSpace: "nowrap", background: isDark ? "rgba(255,255,255,.06)" : "#eef2f6", borderRadius: 999, padding: "2px 9px" }}>{q.sourceKeyword}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}
